<template>
  <div :class="['pt-community', { 'pt-community__carousel': carousel }]">
    <div v-if="!titleHided" class="pt-community__header">
      <h2
        v-if="defaultCommunity.title"
        class="pt-community__title"
        v-text="defaultCommunity.title"
      />
    </div>
    <ClientOnly>
      <template #fallback>
        <div class="relative w-full flex gap-10 justify-around">
          <div
            v-for="person in componentWidth"
            :key="person"
            class="flex-col justify-center items-center gap-5 animate-pulse first:flex hidden xs:[&:nth-of-type(2)]:flex md:[&:nth-of-type(3)]:flex 2lg:last:flex"
          >
            <div class="pt-community__content-picture bg-gray-soft" />
            <span class="w-20 h-3 rounded bg-gray/70" />
            <span class="w-screen max-w-32 h-3 rounded bg-gray/70" />
          </div>
        </div>
      </template>
      <Splide ref="communityCarousel" class="w-full my-4" :options="options" :has-track="false">
        <SplideTrack>
          <SplideSlide
            v-for="(person, index) in personsContainer"
            :key="`container_${index}`"
            data-splide-interval="4000"
          >
            <div
              :class="[
                'pt-community__content content-multiple',
                {
                  'content-carousel': carousel
                },
                {
                  [divisorHided
                    ? ''
                    : `pt-community__divisor${showingCarousel ? '-carousel' : ''}`]:
                    index !== personsContainer?.length - 1
                }
              ]"
            >
              <component
                :is="person.personSlug && person.vip ? NuxtLink : 'div'"
                class="pt-community__content-person"
                :to="person.personSlug || null"
              >
                <NuxtImg
                  v-if="person.image"
                  :key="person.image.url"
                  class="pt-community__image"
                  :class="
                    modeCommunity ? 'pt-community__image-community' : 'pt-community__image-filter'
                  "
                  :title="person.image.alternativeText || person.name"
                  :src="person.image.url"
                  :alt="person.image.alternativeText || 'autor'"
                  loading="lazy"
                />
                <span
                  v-if="person.name"
                  class="line-clamp-2"
                  :class="
                    modeCommunity
                      ? 'pt-community__content-community-name'
                      : 'pt-community__content-name '
                  "
                  v-text="person.name"
                />
              </component>
              <component
                :is="person.articleSlug ? NuxtLink : 'span'"
                v-if="person.description && !modeCommunity"
                :to="person.articleSlug || null"
                class="pt-community__content-description"
                ><h3 class="pt-community__content-description--text" v-text="person.description" />
              </component>
            </div>
          </SplideSlide>
        </SplideTrack>
        <button class="splide__toggle hidden" type="button" />
      </Splide>
    </ClientOnly>
  </div>
</template>
<script setup>
import { watchOnce } from '@vueuse/core';

const props = defineProps({
  people: {
    type: Array,
    default: undefined,
    validator: val => val.every(person => ({ slug: person.slug, image: person.image }))
  },
  title: {
    type: String,
    default: undefined
  },
  link: {
    type: Object,
    default: undefined,
    validator: ({ label, url, icon }) => ({ label, url, icon })
  },
  carousel: {
    type: Boolean,
    default: false
  },
  modeCommunity: {
    type: Boolean,
    default: false
  },
  titleHided: {
    type: Boolean,
    default: false
  },
  divisorHided: {
    type: Boolean,
    default: false
  },
  isBlog: {
    type: Boolean,
    default: false
  }
});

const { t } = useI18n();
const store = useStore();
const { elementSize, windowWidth } = useScreen();
const communityCarousel = useTemplateRef('communityCarousel');

const NuxtLink = resolveComponent('NuxtLink');

const options = ref({
  perPage: 1,
  gap: '1rem',
  rewind: true,
  arrows: false,
  pagination: true
});

const defaultCommunity = computed(() => {
  const {
    our_community: { title, link },
    our_community_items: items
  } = store.getters['strapi/getByField']('common');

  const dataItem = items.map(item =>
    item.post ? { ...item.post, author_role: [item.post.author] } : item.article
  );
  return {
    people: (() => {
      const mapPeople = data => ({
        personSlug: data.slugAuthor
          ? `${AUTHOR_BASE_URL}/${data.slugAuthor}`
          : data?.author_role && data?.author_role[0]?.slug
            ? `${AUTHOR_BASE_URL}/${data.author_role[0].slug}`
            : undefined,
        articleSlug: getSlugArticle(data),
        image: data.author_role ? data.author_role[0]?.image : data.image,
        name: data.author_role
          ? data.author_role[0]?.name
          : (data.name ?? t('author.authorNameDefault')),
        description: data.title ?? t('author.authorDescriptionDefault'),
        vip: data.author_role[0]?.vip
      });
      const setterPeople =
        props.people?.map(person => mapPeople(person)) ??
        dataItem?.map(person => (person ? mapPeople(person) : {}));
      return setterPeople;
    })(),
    title: props.title ?? title ?? '',
    link: props.link ?? link
  };
});
const showingCarousel = computed(() => props.carousel);
const personsContainer = computed(() =>
  props.isBlog ? props.people : defaultCommunity.value.people
);
const componentWidth = computed(() => {
  const getPerPageByWidth = width => {
    if (width >= 1168) return 4;
    if (width >= 736) return 3;
    if (width >= 393) return 2;
    return 1;
  };
  const container = communityCarousel.value;
  if (!container?.root) return showingCarousel.value ? 2 : personsContainer.value.length;
  const { width } = elementSize(container.root);
  return getPerPageByWidth(width.value);
});

const getSlugArticle = item => {
  let baseRoute = NEWSROOMS_BASE_URL;
  if (item.category?.section) {
    baseRoute = item.category.section;
  } else if (item.blog?.slug) {
    baseRoute = `${COMMUNITY_BASE_URL}/${item.blog.slug}`;
  }
  return `${baseRoute}/${item.slug}`;
};
const updateOptions = () => (options.value.perPage = componentWidth.value);
watch(windowWidth, updateOptions, { immediate: true });
watchOnce(communityCarousel, val => {
  updateOptions();
  val.root.querySelector('.splide__toggle')?.click();
});
</script>

<style src="./style.scss" scoped />
<style lang="scss">
.pt-community {
  & .splide__pagination {
    @apply relative flex gap-2 mt-2.5 -bottom-2.5 justify-center;

    & .splide__pagination__page {
      @apply rounded-full cursor-pointer;

      &.is-active {
        @apply bg-blue;
      }
    }
  }
}
</style>
